import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { motion, useDragControls } from "framer-motion";
import { Sparkles, X, Send, ThumbsUp, ThumbsDown, ArrowRight, GripVertical, Search } from "lucide-react";
import api from "../lib/api";

/* Ask TravelerPulse — site-wide contextual Ask-a-Question panel (owner spec).
   Knows the current page (airport/airline/city/article), answers in-page,
   supports follow-ups, related links, feedback, and closes with X without
   losing scroll position. Panel is draggable by its header so it never
   blocks underlying content. */

function pageContext(pathname, search) {
  const seg = pathname.split("/").filter(Boolean);
  const ctx = { url: pathname + search, page_type: "general" };
  if (seg[0] === "airports" && seg[1]) { ctx.page_type = "airport"; ctx.iata = seg[1].toUpperCase(); }
  else if (seg[0] === "india" && seg[1] === "airports" && seg[2] && !["directory", "routes"].includes(seg[2])) {
    ctx.page_type = "airport";
    const m = seg[2].match(/-([a-z0-9]{3})$/i) || seg[2].match(/^([a-z]{3})$/i);
    if (m) ctx.iata = m[1].toUpperCase();
  }
  else if (seg[0] === "airlines" && seg[1]) { ctx.page_type = "airline"; ctx.airline = seg[1]; }
  else if (seg[0] === "cities" && seg[1]) { ctx.page_type = "city"; ctx.city = decodeURIComponent(seg[1]); }
  else if (seg[0] === "countries" && seg[1]) { ctx.page_type = "country"; ctx.country = decodeURIComponent(seg[1]); }
  else if (seg[0] === "articles" && seg[1]) { ctx.page_type = "article"; ctx.slug = seg[1]; }
  else if (seg[0]) ctx.page_type = seg[0];
  return ctx;
}

const md = {
  a: ({ href, children }) => href?.startsWith("/")
    ? <Link to={href} className="text-blue-700 font-600 underline">{children}</Link>
    : <a href={href} target="_blank" rel="noreferrer" className="text-blue-700 font-600 underline">{children}</a>,
  p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
  ul: ({ children }) => <ul className="list-disc ml-5 mb-2 space-y-1">{children}</ul>,
};

export default function AskTravelPulse() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [turns, setTurns] = useState([]); // {q, answer, links, ai, fb}
  const [sessionId, setSessionId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [ac, setAc] = useState([]); // autocomplete completions
  const acTimer = useRef(null);
  const panelRef = useRef(null);
  const boundsRef = useRef(null);
  const dragControls = useDragControls();
  const ctx = pageContext(location.pathname, location.search);

  useEffect(() => { // bottom-nav "Ask" tab opens the assistant
    const openAsk = () => setOpen(true);
    window.addEventListener("tp:ask-open", openAsk);
    return () => window.removeEventListener("tp:ask-open", openAsk);
  }, []);

  useEffect(() => { // new page => fresh conversation context suggestions
    if (open) api.get("/ask/suggestions", { params: { page_type: ctx.page_type } })
      .then((r) => setSuggestions(r.data.suggestions)).catch(() => setSuggestions([]));
  }, [open, ctx.page_type]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (text) => {
    const question = (text || q).trim();
    if (!question || busy) return;
    setBusy(true); setQ(""); setAc([]);
    setTurns((t) => [...t, { q: question, answer: null }]);
    try {
      const r = await api.post("/ask", { question, session_id: sessionId, context: ctx });
      setSessionId(r.data.session_id);
      setTurns((t) => t.map((x, i) => i === t.length - 1 ? { ...x, ...r.data } : x));
    } catch {
      setTurns((t) => t.map((x, i) => i === t.length - 1
        ? { ...x, answer: "The assistant is temporarily unavailable — Try again later.", links: [] } : x));
    } finally {
      setBusy(false);
      setTimeout(() => panelRef.current?.scrollTo({ top: panelRef.current.scrollHeight, behavior: "smooth" }), 100);
    }
  };

  const onType = (val) => {
    setQ(val);
    clearTimeout(acTimer.current);
    if (val.trim().length < 2) { setAc([]); return; }
    acTimer.current = setTimeout(() => {
      api.get("/ask/autocomplete", { params: { q: val, iata: ctx.iata || undefined } })
        .then((r) => setAc(r.data.suggestions)).catch(() => setAc([]));
    }, 250);
  };

  const feedback = async (i, helpful) => {
    setTurns((t) => t.map((x, idx) => idx === i ? { ...x, fb: helpful ? "up" : "down" } : x));
    try { await api.post("/ask/feedback", { session_id: sessionId || "n/a", question: turns[i].q, helpful }); } catch { /* best-effort */ }
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="ask-travelpulse-button"
          className="fixed bottom-20 right-4 sm:bottom-auto sm:right-5 sm:top-1/2 sm:-translate-y-1/2 z-50 inline-flex items-center gap-2 bg-violet-700 text-white px-4 py-3 font-700 text-sm shadow-lg hover:bg-violet-600 transition-colors rounded-full">
          <Sparkles className="w-4 h-4" /> Ask Travel AI
        </button>
      )}
      {open && (
        <div ref={boundsRef} className="fixed inset-0 z-50 pointer-events-none">
          <motion.div
            drag
            dragListener={false}
            dragControls={dragControls}
            dragConstraints={boundsRef}
            dragMomentum={false}
            dragElastic={0}
            className="pointer-events-auto absolute bottom-0 right-0 sm:bottom-auto sm:top-[14vh] sm:right-5 w-full sm:w-[420px] max-h-[80vh] sm:max-h-[72vh] flex flex-col bg-white border border-zinc-300 shadow-2xl"
            data-testid="ask-travelpulse-panel">
            <div
              onPointerDown={(e) => dragControls.start(e)}
              className="flex items-center justify-between px-4 py-3 bg-violet-700 text-white shrink-0 cursor-move select-none touch-none"
              data-testid="ask-drag-handle">
              <div className="flex items-center gap-2 font-heading font-900">
                <GripVertical className="w-4 h-4 opacity-60" /><Sparkles className="w-4 h-4" /> Ask TravelerPulse
              </div>
              <button onClick={() => setOpen(false)} onPointerDown={(e) => e.stopPropagation()}
                data-testid="ask-close-button" aria-label="Close"
                className="p-1.5 hover:bg-violet-600 transition-colors cursor-pointer"><X className="w-5 h-5" /></button>
            </div>
          <div ref={panelRef} className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[180px]">
            {turns.length === 0 && (
              <div>
                <p className="text-sm text-zinc-700 mb-3">
                  {ctx.iata ? <>You're viewing <strong>{ctx.iata}</strong> — ask anything about this airport, or any travel question.</>
                    : "Ask anything about this page — or any travel question."}
                </p>
                <div className="space-y-1.5">
                  {suggestions.map((s) => (
                    <button key={s} onClick={() => submit(s)} data-testid="ask-suggestion"
                      className="flex items-center gap-2 w-full text-left text-sm border border-zinc-200 px-3 py-2 hover:border-violet-500 hover:bg-violet-50 transition-colors">
                      <ArrowRight className="w-3.5 h-3.5 text-violet-600 shrink-0" /> {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {turns.map((t, i) => (
              <div key={i} data-testid={`ask-turn-${i}`}>
                <div className="flex items-start justify-between gap-2 text-sm font-700 bg-zinc-100 px-3 py-2">
                  <span className="flex-1">{t.q}</span>
                  <button onClick={() => setTurns((ts) => ts.filter((_, idx) => idx !== i))}
                    data-testid={`ask-turn-close-${i}`} aria-label="Dismiss this answer" title="Dismiss this answer"
                    className="shrink-0 p-0.5 text-zinc-400 hover:text-red-500 transition-colors"><X className="w-4 h-4" /></button>
                </div>
                <div className="text-sm text-zinc-800 mt-2 px-1">
                  {t.answer === null ? <span className="text-muted-foreground animate-pulse">Searching TravelerPulse…</span>
                    : (
                      <>
                        <div className={t.answer.length > 500 && !t.expanded ? "max-h-44 overflow-hidden relative" : ""}>
                          <ReactMarkdown components={md}>{t.answer}</ReactMarkdown>
                          {t.answer.length > 500 && !t.expanded && (
                            <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent" />
                          )}
                        </div>
                        {t.answer.length > 500 && (
                          <button onClick={() => setTurns((ts) => ts.map((x, idx) => idx === i ? { ...x, expanded: !x.expanded } : x))}
                            data-testid={`ask-show-more-${i}`}
                            className="mt-1 text-xs font-700 text-violet-700 hover:underline">
                            {t.expanded ? "Show less" : "Show more"}
                          </button>
                        )}
                      </>
                    )}
                </div>
                {t.links?.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {t.links.map((l) => l.external ? (
                      <a key={l.url} href={l.url} target="_blank" rel="noreferrer"
                        data-testid="ask-google-link"
                        className="flex items-center gap-1.5 text-xs font-700 text-emerald-700 hover:underline">
                        <ArrowRight className="w-3 h-3" /> {l.label}
                      </a>
                    ) : (
                      <Link key={l.url} to={l.url} onClick={() => setOpen(false)}
                        className="flex items-center gap-1.5 text-xs font-700 text-violet-700 hover:underline">
                        <ArrowRight className="w-3 h-3" /> {l.label}
                      </Link>
                    ))}
                  </div>
                )}
                {t.answer && (
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs text-muted-foreground">Helpful?</span>
                    <button onClick={() => feedback(i, true)} className={`p-1 ${t.fb === "up" ? "text-emerald-600" : "text-zinc-400 hover:text-emerald-600"}`} data-testid="ask-thumbs-up"><ThumbsUp className="w-3.5 h-3.5" /></button>
                    <button onClick={() => feedback(i, false)} className={`p-1 ${t.fb === "down" ? "text-red-500" : "text-zinc-400 hover:text-red-500"}`} data-testid="ask-thumbs-down"><ThumbsDown className="w-3.5 h-3.5" /></button>
                  </div>
                )}
              </div>
            ))}
          </div>
          {ac.length > 0 && (
            <div className="border-t border-zinc-200 bg-violet-50/60 shrink-0 max-h-40 overflow-y-auto" data-testid="ask-autocomplete">
              {ac.map((s) => (
                <button key={s} onClick={() => submit(s)} data-testid="ask-autocomplete-item"
                  className="flex items-center gap-2 w-full text-left text-sm px-4 py-2 hover:bg-violet-100 transition-colors">
                  <Search className="w-3.5 h-3.5 text-violet-600 shrink-0" /> {s}
                </button>
              ))}
            </div>
          )}
          <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="flex items-center gap-2 border-t border-zinc-200 p-3 shrink-0">
            <input value={q} onChange={(e) => onType(e.target.value)} data-testid="ask-input"
              placeholder={ctx.iata ? `Ask about ${ctx.iata}…` : "Ask a question – search…"}
              className="flex-1 border border-zinc-300 px-3 py-2.5 text-sm focus:outline-none focus:border-violet-600" />
            <button type="submit" disabled={busy} data-testid="ask-submit"
              className="bg-violet-700 text-white p-2.5 hover:bg-violet-600 disabled:opacity-50 transition-colors">
              <Send className="w-4 h-4" />
            </button>
          </form>
          </motion.div>
        </div>
      )}
    </>
  );
}
